import * as React from "react"
import { useEffect, useState } from "react"
import {
	Alert, Box, Chip, CircularProgress, Divider, Drawer, IconButton,
	Paper, Stack, Table, TableBody, TableCell, TableContainer,
	TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { BACKEND_APIS } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"

import { vtApi as api } from "../api"
import { fmtDateTime, isoMinusDays, todayISO } from "../metricsUtils"


// Counterpart to Operator Metrics: that page measures TAGGING time, this one
// measures MAPPING throughput — how many resources each mapper reviewed in the
// period and at what rate. Both default to a 30-day window so switching between
// them doesn't move the goalposts.

// Rate is only meaningful over manual work, so the caveat travels with the page
// instead of living in a service docstring nobody reading the table will open.
const RATE_CAVEAT = 'Rate = manual validations (Review Queue + leaf queue) per active UTC hour. '
	+ 'Bulk sweeps, brand-resolve batches, machine events and pre-deploy (backfill) '
	+ 'events are counted in the totals but excluded from the rate — so the rate is '
	+ 'exact from the deploy onwards, and historical rows show totals only.'


// Robots and one-off scripts got one table row each, which drowned the mappers the
// page is about: `auto-heal-brand-gap`, `auto-validate-*`, plus historical actors
// like `brand-backfill-2026-07` that no longer exist in the code and can only ever
// show up in backfilled rows. They are collapsed into a single expandable row.
//
// The split is by `id_user`, NOT by name: an actor with no id_user matched no
// SM_USER, which is exactly what every machine actor looks like — and a name
// whitelist would silently miss the next script someone writes. A human whose
// email failed to match (a typo, say) would land here too, so a NULL-user row that
// DOES carry manual work stays visible on its own: that is human throughput, and
// hiding it would understate someone's day.
function splitActors(rows) {
	const automated = rows.filter(r => r.id_user == null && r.manual_events === 0)
	const people = rows.filter(r => !automated.includes(r))
	return { people, automated }
}

function aggregateAutomated(rows) {
	const sum = key => rows.reduce((acc, r) => acc + (r[key] || 0), 0)
	const lastActivity = rows.map(r => r.last_activity).filter(Boolean).sort().pop() || null
	return {
		count: rows.length,
		// Event counts are additive. `distinct_resources` is not, strictly: a resource
		// touched by two different processes counts once per process, so this total can
		// exceed the real number of resources. The cell says so in its tooltip instead
		// of pretending to a precision this view cannot compute.
		distinct_resources: sum('distinct_resources'),
		bulk_events: sum('bulk_events'),
		brand_resolve_events: sum('brand_resolve_events'),
		machine_events: sum('machine_events'),
		backfill_events: sum('backfill_events'),
		total_events: sum('total_events'),
		last_activity: lastActivity,
	}
}


export default function MappingMetricsPage() {
	const { showToast } = useAppToast()

	const [startDate, setStartDate] = useState(isoMinusDays(30))
	const [endDate, setEndDate] = useState(todayISO())
	const [loading, setLoading] = useState(false)
	const [rows, setRows] = useState([])
	const [byDate, setByDate] = useState([])
	// Collapsed by default: the question this page answers is about people.
	const [showAutomated, setShowAutomated] = useState(false)

	// Drill-down target. Identity is (id_user, email), not id_user alone: the
	// same actor can hold two rows — one with id_user resolved for their queue
	// work, one NULL for bulk or backfilled events — and both must be openable.
	const [selected, setSelected] = useState(null)
	const [detail, setDetail] = useState([])
	const [detailLoading, setDetailLoading] = useState(false)

	const rangeParams = () => {
		const params = new URLSearchParams()
		if (startDate) params.set('start_date', startDate)
		if (endDate)   params.set('end_date', endDate)
		return params
	}

	const load = async () => {
		setLoading(true)
		const r = await api.get(`${BACKEND_APIS.VT}/admin/mapping-metrics?${rangeParams().toString()}`)
		setLoading(false)
		if (!r.ok) {
			showToast.error('Failed to load mapping metrics: ' + r.error)
			setRows([]); setByDate([])
			return
		}
		setRows(r.data?.items || [])
		setByDate(r.data?.by_date || [])
	}

	useEffect(() => { load() }, [startDate, endDate])

	const openDetail = async (row) => {
		setSelected(row)
		setDetailLoading(true)
		const params = rangeParams()
		// A row without id_user is identified by its verbatim actor string.
		if (row.id_user) params.set('id_user', String(row.id_user))
		else if (row.email) params.set('email', row.email)
		const r = await api.get(`${BACKEND_APIS.VT}/admin/mapping-metrics/detail?${params.toString()}`)
		setDetailLoading(false)
		if (!r.ok) { showToast.error('Failed to load detail: ' + r.error); setDetail([]); return }
		setDetail(r.data?.items || [])
	}
	const closeDetail = () => { setSelected(null); setDetail([]) }

	const { people, automated } = splitActors(rows)
	const totals = aggregateAutomated(automated)

	return <PageWrapper
		{...vtPageProps('Mapping Metrics', 'Per-mapper resource reviews — totals in the period and resources/hour')}
	>
		<Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
			<TextField size="small" label="From" type="date" sx={{ width: 180 }}
				value={startDate} onChange={e => setStartDate(e.target.value)}
				InputLabelProps={{ shrink: true }} />
			<TextField size="small" label="To" type="date" sx={{ width: 180 }}
				value={endDate} onChange={e => setEndDate(e.target.value)}
				InputLabelProps={{ shrink: true }} />
		</Stack>

		<Alert severity="info" variant="outlined" sx={{ mb: 2 }}>{RATE_CAVEAT}</Alert>

		{loading ? (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
				<CircularProgress size={22} /><Typography variant="body2">Loading…</Typography>
			</Box>
		) : rows.length === 0 ? (
			<Alert severity="info">No review activity in this date range.</Alert>
		) : (
			<>
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Mapper</TableCell>
								<Tooltip title="Distinct resources touched in the range. The columns to its right count EVENTS, so they don't add up to this number — a resource reviewed twice is 2 events, 1 resource.">
									<TableCell align="right">Resources</TableCell>
								</Tooltip>
								<Tooltip title="Manual events: Review Queue + orphan-leaf queue. The only ones that feed the rate.">
									<TableCell align="right">Manual</TableCell>
								</Tooltip>
								<Tooltip title="Bulk sweep events (bulk-cleanup / bulk-fanout).">
									<TableCell align="right">Bulk</TableCell>
								</Tooltip>
								<Tooltip title="Brand-resolve batch events (endpoint or auto-map-keyword CLI).">
									<TableCell align="right">Brand</TableCell>
								</Tooltip>
								<Tooltip title="Machine events: auto-heal, auto-validate scripts, AI sync.">
									<TableCell align="right">Machine</TableCell>
								</Tooltip>
								<Tooltip title="Events reconstructed from VT_RESOURCES when the ledger was created. They cannot be classified as manual or machine, so they never feed the rate.">
									<TableCell align="right">Backfill</TableCell>
								</Tooltip>
								<Tooltip title="Distinct UTC hours with at least one manual event. A mapper working 10 minutes within an hour bills that whole hour.">
									<TableCell align="right">Active hours</TableCell>
								</Tooltip>
								<TableCell align="right">Res / hour</TableCell>
								<TableCell>Last activity</TableCell>
								<TableCell align="right"></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{people.map(row => (
								<MapperRow key={row.id_user ?? `email:${row.email}`} row={row} onOpen={openDetail} />
							))}
							{automated.length > 0 && (
								<TableRow hover sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}
									onClick={() => setShowAutomated(v => !v)}>
									<TableCell>
										<Stack direction="row" spacing={1} alignItems="center">
											{showAutomated ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												Automated processes
											</Typography>
											<Tooltip title="Robots and one-off scripts: brand healing, the auto-validate scripts, bulk cleanups and historical backfill actors. Grouped because they are not mappers — click to see them one by one.">
												<Chip size="small" variant="outlined" label={`${totals.count} processes`} sx={{ height: 18 }} />
											</Tooltip>
										</Stack>
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 600 }}>
										<Tooltip title="Sum across processes. A resource touched by two different processes counts in each, so this can exceed the real number of resources.">
											<span>{totals.distinct_resources}</span>
										</Tooltip>
									</TableCell>
									<TableCell align="right">0</TableCell>
									<TableCell align="right" sx={{ color: 'text.secondary' }}>{totals.bulk_events}</TableCell>
									<TableCell align="right" sx={{ color: 'text.secondary' }}>{totals.brand_resolve_events}</TableCell>
									<TableCell align="right" sx={{ color: 'text.secondary' }}>{totals.machine_events}</TableCell>
									<TableCell align="right" sx={{ color: 'text.secondary' }}>{totals.backfill_events}</TableCell>
									<TableCell align="right">—</TableCell>
									<TableCell align="right">
										<Tooltip title="Automated work never produces a rate — that is the point of separating it.">
											<Typography variant="caption" sx={{ color: 'text.disabled' }}>n/a</Typography>
										</Tooltip>
									</TableCell>
									<TableCell><Typography variant="caption">{fmtDateTime(totals.last_activity)}</Typography></TableCell>
									<TableCell align="right"></TableCell>
								</TableRow>
							)}
							{showAutomated && automated.map(row => (
								<MapperRow key={row.id_user ?? `email:${row.email}`} row={row} onOpen={openDetail} nested />
							))}
						</TableBody>
					</Table>
				</TableContainer>

				<Box sx={{ mt: 2, maxWidth: 560 }}>
					<ByDateTable rows={byDate} />
				</Box>
			</>
		)}

		<MapperDetailDrawer
			mapper={selected} rows={detail} loading={detailLoading} onClose={closeDetail}
		/>
	</PageWrapper>
}


function MapperRow({ row, onOpen, nested = false }) {
	return <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onOpen(row)}>
		<TableCell sx={nested ? { pl: 5 } : undefined}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography variant="body2" sx={{ fontWeight: nested ? 400 : 600 }}>{row.email}</Typography>
				{/* "backfill only" wins over "machine": right after the deploy every
				    actor has zero manual events, and calling a human operator a machine
				    because their pre-ledger work couldn't be classified would be wrong. */}
				{row.is_backfill_only ? (
					<Tooltip title="All of this actor's events in the range come from the pre-deploy backfill, which cannot be classified as manual or machine — hence no rate.">
						<Chip size="small" variant="outlined" label="backfill only" sx={{ height: 18 }} />
					</Tooltip>
				) : row.is_machine_only ? (
					<Chip size="small" variant="outlined" label="machine" sx={{ height: 18 }} />
				) : null}
			</Stack>
		</TableCell>
		<TableCell align="right" sx={{ fontWeight: nested ? 400 : 600 }}>{row.distinct_resources}</TableCell>
		<TableCell align="right">{row.manual_events}</TableCell>
		<TableCell align="right" sx={{ color: 'text.secondary' }}>{row.bulk_events}</TableCell>
		<TableCell align="right" sx={{ color: 'text.secondary' }}>{row.brand_resolve_events}</TableCell>
		<TableCell align="right" sx={{ color: 'text.secondary' }}>{row.machine_events}</TableCell>
		<TableCell align="right" sx={{ color: 'text.secondary' }}>{row.backfill_events}</TableCell>
		<TableCell align="right">{row.active_hours || '—'}</TableCell>
		<TableCell align="right">
			{row.resources_per_hour != null
				? <Chip size="small" color="primary" variant="outlined"
					label={row.resources_per_hour.toFixed(1)} />
				: <Tooltip title="No manual work in this range, so there is no rate to compute.">
					<Typography variant="caption" sx={{ color: 'text.disabled' }}>n/a</Typography>
				</Tooltip>}
		</TableCell>
		<TableCell><Typography variant="caption">{fmtDateTime(row.last_activity)}</Typography></TableCell>
		<TableCell align="right">
			<Tooltip title="Event-by-event detail">
				<IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpen(row) }}>
					<OpenInNewIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		</TableCell>
	</TableRow>
}


function ByDateTable({ rows }) {
	if (!rows || rows.length === 0) return null
	return <TableContainer component={Paper} variant="outlined">
		<Table size="small">
			<TableHead>
				<TableRow>
					<TableCell>Day (UTC)</TableCell>
					<TableCell align="right">Manual events</TableCell>
					<TableCell align="right">All events</TableCell>
					<TableCell align="right">Resources</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{rows.map(r => (
					<TableRow key={r.day} hover>
						<TableCell>{r.day}</TableCell>
						<TableCell align="right" sx={{ fontWeight: 600 }}>{r.manual_events}</TableCell>
						<TableCell align="right" sx={{ color: 'text.secondary' }}>{r.total_events}</TableCell>
						<TableCell align="right">{r.distinct_resources}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	</TableContainer>
}


function MapperDetailDrawer({ mapper, rows, loading, onClose }) {
	const open = Boolean(mapper)
	return <Drawer anchor="right" open={open} onClose={onClose}
		PaperProps={{ sx: { width: { xs: '100%', sm: 680 } } }}>
		<Box sx={{ p: 2 }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
				<Typography variant="h6" noWrap>{mapper?.email}</Typography>
				<IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
			</Stack>
			{mapper && (
				<Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
					<Chip size="small" label={`${mapper.distinct_resources} resources`} />
					<Chip size="small" label={`${mapper.manual_events} manual`} />
					{mapper.resources_per_hour != null
						? <Chip size="small" color="primary" variant="outlined"
							label={`${mapper.resources_per_hour.toFixed(1)} res/h`} />
						: <Chip size="small" variant="outlined"
							label={mapper.is_backfill_only ? 'no rate (backfill only)' : 'no rate (machine / bulk only)'} />}
					<Chip size="small" variant="outlined" label={`${mapper.total_events} events`} />
				</Stack>
			)}
			<Divider sx={{ mb: 1.5 }} />
			{loading ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
					<CircularProgress size={20} /><Typography variant="body2">Loading detail…</Typography>
				</Box>
			) : rows.length === 0 ? (
				<Alert severity="info">No events in this range.</Alert>
			) : (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Resource</TableCell>
								<TableCell>Retailer</TableCell>
								<TableCell>Source</TableCell>
								<TableCell>Reviewed at (UTC)</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map(r => (
								<TableRow key={r.id_review} hover>
									<TableCell>
										<Typography variant="body2">#{r.id_resource}</Typography>
										{r.merged_into && (
											<Typography variant="caption" sx={{ color: 'text.disabled' }}>
												merged into #{r.merged_into}
											</Typography>
										)}
									</TableCell>
									<TableCell>
										<Typography variant="caption">{r.retailer_slug || '—'}</Typography>
									</TableCell>
									<TableCell>
										<Chip size="small" label={r.source} sx={{ height: 20 }}
											color={r.is_manual ? 'primary' : 'default'}
											variant="outlined" />
									</TableCell>
									<TableCell><Typography variant="caption">{fmtDateTime(r.reviewed_at)}</Typography></TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	</Drawer>
}
